import { Injectable } from "@angular/core";
import { PageConfig } from "../models/page.model";
import { PageStateService } from "../states/page-state.service";
import { computed, Signal } from "@angular/core";

@Injectable({
  providedIn: "root",
})
export class PageService {
  constructor(private pageState: PageStateService) {
    console.log("[PageService] Service initialized");
  }

  /**
   * Get a signal for a page configuration
   * @param pageId ID of the page to retrieve
   * @returns Signal with the page configuration
   */
  pageConfig(pageId: string): Signal<PageConfig | undefined> {
    console.log(`[PageService] Requesting config for page ${pageId}`);

    return computed(() => {
      const page = this.pageState.getPage(pageId);

      if (page) {
        console.log(`[PageService] Found existing config for ${pageId}`);
        return page;
      }

      console.warn(
        `[PageService] No config found, generating placeholder for ${pageId}`
      );
      return this.generatePlaceholderPage(pageId);
    });
  }

  /**
   * Get all page configurations
   */
  get allPages() {
    return this.pageState.allPages;
  }

  /**
   * Get pages by their IDs (synchronous implementation)
   * @param pageIds Array of page IDs to retrieve
   * @returns Array of page configurations
   */
  getPagesSync(pageIds: string[]): PageConfig[] {
    console.log(`[PageService] Getting pages for IDs: ${pageIds.join(", ")}`);

    return pageIds
      .map((id) => {
        // First check the state
        const page = this.pageState.getPage(id);
        if (page) {
          console.log(`[PageService] Found cached page for ID: ${id}`);
          return page;
        }

        // Generate a placeholder page
        console.warn(
          `[PageService] No page found for ID: ${id}, generating placeholder`
        );
        const generatedPage = this.generatePlaceholderPage(id);
        this.pageState.addPage(id, generatedPage);
        return generatedPage;
      })
      .filter(Boolean) as PageConfig[];
  }

  /**
   * Generate a placeholder page based on the page ID
   * @param pageId ID of the page to generate
   * @returns Generated page configuration
   */
  private generatePlaceholderPage(id: string): PageConfig {
    return {
      pageId: id,
      title: `Page ${id}`,
      description: `Auto-generated page for ${id}`,
      viewId: "view_mock",
      icon: "dashboard",
      order: 1,
      layout: [],
    };
  }

  /**
   * Add or update a page in the state
   * @param page Page configuration to store
   */
  setPageConfig(page: PageConfig): void {
    console.log(
      `[PageService] Setting page configuration for ID: ${page.pageId}`
    );
    this.pageState.addPage(page.pageId, page);
  }

  /**
   * Update specific properties of a page configuration
   * @param id Page ID to update
   * @param updates Partial page configuration with properties to update
   */
  updatePageConfig(id: string, updates: Partial<PageConfig>): void {
    console.log(`[PageService] Updating page configuration for ID: ${id}`);
    this.pageState.updatePage(id, updates);
  }

  /**
   * Delete a page configuration
   * @param id Page ID to delete
   */
  deletePage(id: string): void {
    console.log(`[PageService] Deleting page configuration for ID: ${id}`);
    this.pageState.deletePage(id);
  }

  /**
   * Clear all page configurations
   */
  clearAllPages(): void {
    console.log(`[PageService] Clearing all page configurations`);
    this.pageState.clearAllPages();
  }
}
